import { FormDropdown } from '../../../../shared/components/cells/FormDropDown/FormDropdown';
import { FormRow } from '../../../../shared/components/atoms/FormRow';
import { InputText } from '../../atoms/Input/InputText';
import { CollaborationApiS, CollaborationConstantS } from '../../../../features/Collaboration/outbound/share';
import { entries, map, pipe, prepend, toArray } from '@fxts/core';
import { ButtonCheckbox, InputCheckboxChangeEvent } from '../../atoms/ButtonCheckbox/ButtonCheckbox';
import { html, View } from 'rune-ts';
import klass from './Collaboration.module.scss';
import { font } from '../../../../shared/typography';
import { Textarea } from '../../atoms/Textarea/Textarea';
import { CollaborationTexts } from './CollaborationTexts';
import { ButtonAction } from '../../../../shared/components/atoms/ButtonAction/ButtonAction';
import { CollaborationForm } from '../../../../features/Collaboration/type';
import { CollaborationFormSchema } from '../../../../features/Collaboration/validation';
import { ConfigSentryF } from '../../../../../../modules/Config/Sentry/F/Function/module/ConfigSentryF';
import { Alert } from '../../../../shared/components/cells/Alert/Alert';
import { preventEscapeErrorJson } from '../../../../shared/util/preventEscapeErrorJson';
import { MarppleShopLoadingView } from '../../atoms/Loading/MarppleShopLoadingView';

export interface CollaborationControllerData {
  creator_name: string;
  is_mobile: boolean;
}

export class CollaborationController {
  public Loading = new MarppleShopLoadingView();

  private creator_name: string;

  // dropdown
  private collaborationCategoryDropdown: FormDropdown<string>;

  // textarea
  private proposalTextAreaView: Textarea;

  // input
  public categoryEtcInputView: InputText;
  private brandNameInputView: InputText;
  private brandUrlInputView: InputText;
  private managerNameInputView: InputText;
  private managerTelInputView: InputText;
  private managerEmailInputView: InputText;

  // checkbox
  private collaborationTypeCheckboxViews: ButtonCheckbox[];

  // form row
  public creatorFormRow: FormRow;
  public collaborationTypeFormRow: FormRow;
  public collaborationFormRow: FormRow;
  public proposalFormRow: FormRow;
  public brandNameFormRow: FormRow;
  public brandUrlFormRow: FormRow;
  public managerNameFormRow: FormRow;
  public managerTelFormRow: FormRow;
  public managerEmailFormRow: FormRow;

  public readonly texts = CollaborationTexts.texts();

  // button
  public submitButton: ButtonAction;
  public cancelButton: ButtonAction;

  static getFormChangeEvents() {
    return {
      DropdownChangeEvent: FormDropdown.AfterRedrawEvent,
      TextAreaChangeEvent: Textarea.ChangeEvent,
      InputChangeEvent: 'change',
      CheckboxChangeEvent: InputCheckboxChangeEvent,
    } as const;
  }

  constructor(data: { creator_name: string; is_mobile: boolean }) {
    const { texts } = this;

    // 크리에이터 명
    this.creator_name = data.creator_name;

    this.creatorFormRow = new FormRow({
      label: texts.creator_name,
      content: this.creator_name,
    });

    // 제안 종류
    this.collaborationTypeCheckboxViews = this._makeButtonCheckboxViews(data.is_mobile);

    this.collaborationTypeFormRow = new FormRow({
      label: texts.collaboration_type,
      content: html`<div class="${klass.checkbox_view_wrapper}">
        ${this.collaborationTypeCheckboxViews}
      </div> `,
      field_right: texts.collaboration_type_description,
      required: true,
    });

    // 협업 카테고리
    this.collaborationCategoryDropdown = this._makeCategoryDropDownView(data.is_mobile);

    this.categoryEtcInputView = new InputText({
      name: 'category_etc',
      placeholder: texts.collaboration_category_etc_placeholder,
      custom_klass: klass.etc,
    });

    this.collaborationFormRow = new FormRow({
      label: texts.collaboration_category,
      content: html` <div class="${klass.dropdown}">${this.collaborationCategoryDropdown}</div> `,
      required: true,
    });

    // 제안 내용
    this.proposalTextAreaView = new Textarea({
      name: 'proposal_content',
      placeholder: texts.proposal_content_placeholder,
      rows: data.is_mobile ? 5 : 6,
      is_show_count: !data.is_mobile,
    });

    this.proposalFormRow = new FormRow({
      label: texts.proposal_content,
      content: this.proposalTextAreaView,
      required: true,
    });

    // 브랜드명
    this.brandNameInputView = new InputText({
      name: 'brand_name',
      placeholder: texts.brand_name_placeholder,
      required: true,
    });

    this.brandNameFormRow = new FormRow({
      label: texts.brand_name,
      content: this.brandNameInputView,
      required: true,
    });

    // 자사몰 홈페이지 URL
    this.brandUrlInputView = new InputText({
      name: 'brand_url',
      placeholder: texts.brand_url_placeholder,
      required: true,
    });

    this.brandUrlFormRow = new FormRow({
      label: texts.brand_url,
      content: this.brandUrlInputView,
      required: true,
    });

    // 담당자 성함
    this.managerNameInputView = new InputText({
      name: 'manager_name',
      placeholder: texts.manager_name_placeholder,
    });

    this.managerNameFormRow = new FormRow({
      label: texts.manager_name,
      content: this.managerNameInputView,
      required: true,
    });

    // 담당자 연락처
    this.managerTelInputView = new InputText({
      name: 'manager_tel',
      placeholder: texts.manager_tel_placeholder,
    });

    this.managerTelFormRow = new FormRow({
      label: texts.manager_tel,
      content: this.managerTelInputView,
      required: true,
    });

    // 담당자 이메일
    this.managerEmailInputView = new InputText({
      name: 'manager_email',
      placeholder: texts.manager_email_placeholder,
    });

    this.managerEmailFormRow = new FormRow({
      label: texts.manager_email,
      content: this.managerEmailInputView,
      required: true,
    });

    // 협업 제안하기
    this.submitButton = new ButtonAction({
      size: 'large',
      label: this.texts.proposal_collaboration,
      type: 'disabled',
      klass: klass.submit_button,
    });

    // 취소
    this.cancelButton = new ButtonAction({
      size: 'large',
      label: this.texts.cancel,
      type: 'line',
      klass: klass.cancel_button,
    });
  }

  private _makeCategoryDropDownView = (is_mobile: boolean) => {
    const category_texts = this.texts.category;

    const category_items = pipe(
      CollaborationConstantS.COLLABORATION_CATEGORY,
      entries,
      map(([key, category]) => ({
        key: category,
        name: html`${category_texts[key]}`,
        value: category,
      })),
      prepend(
        FormDropdown.hiddenItem<string>({
          key: '',
          name: html`<span class="${klass.gray}">${this.texts.collaboration_category_placeholder}</span> `,
          value: '',
        }),
      ),
      toArray,
    );

    return new FormDropdown<string>(
      {
        name: 'category',
        items: category_items,
        position: 'absolute',
        status: 'ACTIVE',
      },
      {
        is_mobile,
        onSelectHook: (item) => {
          const input_element$ = this.categoryEtcInputView.element();

          if (input_element$ && item.value === '기타') {
            input_element$.style.display = 'block';
          } else {
            input_element$.style.display = 'none';
          }
        },
      },
    );
  };

  private _makeButtonCheckboxViews = (is_mobile: boolean) => {
    const type_texts = this.texts.type;

    return pipe(
      CollaborationConstantS.COLLABORATION_TYPE,
      entries,
      map(
        ([key, type]) =>
          new ButtonCheckbox({
            name: type,
            label: type_texts[key],
            is_mobile,
          }),
      ),
      toArray,
    );
  };

  public makeNotificationHtml = () => {
    const { texts } = this;

    return html`
      <div class="${klass.notification}">
        <div class="${klass.row} ${font('14_medium', '12_medium')}">
          <div class="${klass.point}"></div>
          <span class="${klass.content}"> ${html.preventEscape(texts.announcement.explanation)} </span>
        </div>
        <div class="${klass.row} ${font('14_medium', '12_medium')}">
          <div class="${klass.point} "></div>
          <span class="${klass.content}"> ${html.preventEscape(texts.announcement.response)} </span>
        </div>
        <div class="${klass.row} ${font('14_medium', '12_medium')}">
          <div class="${klass.point}"></div>
          <span class="${klass.content}"> ${html.preventEscape(texts.announcement.rejection)} </span>
        </div>
        <div class="${klass.row} ${font('14_medium', '12_medium')}">
          <div class="${klass.point}"></div>
          <span class="${klass.content}"> ${html.preventEscape(texts.announcement.apply)} </span>
        </div>
      </div>
    `;
  };

  public delegateFormChangeEvent<T extends object>(view: View<T>, listener: VoidFunction) {
    const { TextAreaChangeEvent, CheckboxChangeEvent, DropdownChangeEvent, InputChangeEvent } =
      CollaborationController.getFormChangeEvents();

    view.delegate(TextAreaChangeEvent, Textarea, () => listener());

    view.delegate(DropdownChangeEvent, FormDropdown, () => listener());

    view.delegate(CheckboxChangeEvent, ButtonCheckbox, () => listener());

    view.delegate(InputChangeEvent, InputText, () => listener());
  }

  public delegateSubmitEvent<T extends object>(
    view: View<T>,
    callback: VoidFunction,
    submitProposal = CollaborationApiS.postCollaboration,
  ) {
    view.delegate('click', `.${klass.submit_button}`, async () => {
      try {
        const result = this.safeParseFormData();

        this.Loading.show();

        if (!result.success) {
          return;
        }

        const { data } = await submitProposal(result.data);

        if (!data.success) {
          throw new Error('협업 제안하기 airtable 제출 실패');
        }

        await Alert.open(
          preventEscapeErrorJson({
            title: this.texts.alert.success_title,
            message: this.texts.alert.success_description,
          }),
        );

        callback();
      } catch (e) {
        ConfigSentryF.error(e);
        return Alert.open(
          preventEscapeErrorJson({
            title: ET('mps2::common::error_title'),
            message: ET('mps2::common::error_message'),
          }),
        );
      } finally {
        this.Loading.hide();
      }
    });
  }

  public delegateCancelEvent<T extends object>(view: View<T>, cancelFn: VoidFunction) {
    view.delegate('click', `.${klass.cancel_button}`, cancelFn);
  }

  public changeSubmitButtonByValidation = () => {
    if (!this.submitButton.isRendered()) {
      return;
    }

    const result = this.safeParseFormData();

    this.submitButton.setTypeOption(result.success ? 'blue' : 'disabled');
  };

  public safeParseFormData() {
    return CollaborationFormSchema.safeParse(this.currentData);
  }

  get currentData() {
    return {
      creator_name: this.creator_name,
      collaboration_types: this.collaborationTypeCheckboxViews
        .filter((checkbox) => checkbox.isChecked())
        .map((checkbox) => checkbox.data.name),
      category: this.collaborationCategoryDropdown.getSelectedItem().value,
      category_etc: this.categoryEtcInputView.getValue(),
      proposal_content: this.proposalTextAreaView.getValue(),
      brand_name: this.brandNameInputView.getValue(),
      brand_url: this.brandUrlInputView.getValue(),
      manager_name: this.managerNameInputView.getValue(),
      manager_tel: this.managerTelInputView.getValue(),
      manager_email: this.managerEmailInputView.getValue(),
    };
  }

  public setCreatorName(creator_name: string) {
    this.creator_name = creator_name;

    this.creatorFormRow.setPropsAndRedraw({
      content: this.creator_name,
    });
  }

  public resetFormData() {
    this.setFormData({
      creator_name: '',
      collaboration_types: [],
      manager_email: '',
      brand_name: '',
      brand_url: '',
      proposal_content: '',
      category: '',
      manager_name: '',
      manager_tel: '',
    });
  }

  public setFormData(params: CollaborationForm) {
    this.creator_name = params.creator_name;
    this.collaborationCategoryDropdown.setSelectedItem(params.category);
    this.categoryEtcInputView.setValue(params.category_etc || '');
    this.proposalTextAreaView.setValue(params.proposal_content);
    this.brandNameInputView.setValue(params.brand_name);
    this.brandUrlInputView.setValue(params.brand_url);
    this.managerNameInputView.setValue(params.manager_name);
    this.managerTelInputView.setValue(params.manager_tel);
    this.managerEmailInputView.setValue(params.manager_email);
    this.collaborationTypeCheckboxViews.forEach((checkbox) => {
      if ((params.collaboration_types as string[]).includes(checkbox.getName())) {
        checkbox.activate();
      } else {
        checkbox.deactivate();
      }
    });
  }
}
